import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

interface PSOResult {
  bestParams: Record<string, number | string>;
  bestTargetValue: number;
  bestPrediction: Record<string, number>;
  iterations: number;
  convergenceHistory: number[];
  particleHistory: Array<Array<{ position: Record<string, number | string>, targetValue: number, prediction: Record<string, number> }>>;
  baselineValue: number; // Baseline-Wert vor Optimierung
  percentageImprovement: number; // Prozentuale Verbesserung
  targetRange: { min: number; max: number }; // Realistischer Wertebereich für das Target
}

interface PSOSettings {
  particles: number;
  iterations: number;
  w: number;
  c1: number;
  c2: number;
  targetVariable: string;
  optimizationDirection: 'maximize' | 'minimize';
}

interface PSOVisualizationProps {
  result: PSOResult;
  psoSettings: PSOSettings;
}

export const PSOVisualization: React.FC<PSOVisualizationProps> = ({ result, psoSettings }) => {
  const [selectedIteration, setSelectedIteration] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [showFinalResult, setShowFinalResult] = useState<boolean>(false);

  // Prepare data for convergence chart - only show data up to selected iteration
  const convergenceData = result.convergenceHistory.slice(0, selectedIteration + 1).map((targetValue, index) => ({
    iteration: index + 1,
    targetValue: targetValue,
    bestSoFar: psoSettings.optimizationDirection === 'maximize' ? 
      Math.max(...result.convergenceHistory.slice(0, index + 1)) :
      Math.min(...result.convergenceHistory.slice(0, index + 1))
  }));

  // Animate through iterations with fastest speed
  const startAnimation = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setSelectedIteration(0);
    setShowFinalResult(false);
    
    const totalIterations = result.convergenceHistory.length;
    let currentIteration = 0;
    
    const animationInterval = setInterval(() => {
      currentIteration += 1;
      setSelectedIteration(currentIteration);
      
      if (currentIteration >= totalIterations - 1) {
        clearInterval(animationInterval);
        setIsAnimating(false);
        setShowFinalResult(true);
      }
    }, 100); // Schnellste Geschwindigkeit: 100ms pro Iteration
  };

  // Stop animation function
  const stopAnimation = () => {
    setIsAnimating(false);
  };

  // Reset to beginning
  const resetAnimation = () => {
    setSelectedIteration(0);
    setShowFinalResult(false);
    setIsAnimating(false);
  };

  // Get current particle data for visualization
  const getCurrentParticleData = () => {
    if (result.particleHistory.length === 0) return [];
    
    const iterationData = result.particleHistory[selectedIteration] || result.particleHistory[0];
    return iterationData.map((particle, index) => ({
      id: index,
      x: index, // Simple X positioning
      y: particle.targetValue * 100, // Scale target value for visibility
      targetValue: particle.targetValue
    }));
  };

  // Get radar chart data for variable parameters including target value
  const getRadarChartData = () => {
    // Immer Radar Chart anzeigen - verwende verfügbare Daten oder Interpolation
    if (showFinalResult) {
      // Finale optimale Konfiguration anzeigen
      const radarData: Array<{ parameter: string, value: number, normalizedValue: number }> = [];
      
      // Füge optimale Parameter hinzu
      Object.entries(result.bestParams).forEach(([paramName, paramValue]) => {
        if (typeof paramValue === 'number') {
          const normalizedValue = Math.max(0, Math.min(100, (paramValue + Math.abs(paramValue)) * 10 % 100));
          radarData.push({
            parameter: paramName.length > 10 ? paramName.substring(0, 10) + '...' : paramName,
            value: paramValue,
            normalizedValue: normalizedValue
          });
        }
      });
      
      // Füge Target-Variable hinzu
      const targetValue = result.bestTargetValue;
      radarData.push({
        parameter: `🎯 ${psoSettings.targetVariable.length > 8 ? psoSettings.targetVariable.substring(0, 8) + '...' : psoSettings.targetVariable}`,
        value: targetValue,
        normalizedValue: targetValue * 100
      });
      
      return radarData;
    }
    
    // Für aktuelle Iteration: verwende verfügbare Partikel-Daten oder interpoliere
    if (selectedIteration < result.particleHistory.length && result.particleHistory[selectedIteration]) {
      // Partikel-Daten verfügbar - verwende beste Partikel
      const currentIteration = result.particleHistory[selectedIteration];
      const bestParticle = currentIteration.reduce((best, current) => {
        const isBetter = psoSettings.optimizationDirection === 'maximize' ? 
          current.targetValue > best.targetValue : current.targetValue < best.targetValue;
        return isBetter ? current : best;
      });
      
      const radarData: Array<{ parameter: string, value: number, normalizedValue: number }> = [];
      
      Object.entries(bestParticle.position).forEach(([paramName, paramValue]) => {
        if (typeof paramValue === 'number') {
          const normalizedValue = Math.max(0, Math.min(100, (paramValue + Math.abs(paramValue)) * 10 % 100));
          radarData.push({
            parameter: paramName.length > 10 ? paramName.substring(0, 10) + '...' : paramName,
            value: paramValue,
            normalizedValue: normalizedValue
          });
        }
      });
      
      // Füge Target-Variable hinzu
      const targetValue = bestParticle.targetValue;
      radarData.push({
        parameter: `🎯 ${psoSettings.targetVariable.length > 8 ? psoSettings.targetVariable.substring(0, 8) + '...' : psoSettings.targetVariable}`,
        value: targetValue,
        normalizedValue: targetValue * 100
      });
      
      return radarData;
    } else {
      // Keine Partikel-Daten verfügbar - interpoliere basierend auf Konvergenz-Historie
      const radarData: Array<{ parameter: string, value: number, normalizedValue: number }> = [];
      const convergenceFactor = selectedIteration / (result.convergenceHistory.length - 1);
      const currentTargetValue = result.convergenceHistory[selectedIteration] || 0;
      
      // Interpoliere Parameter zwischen Start und finalen optimalen Werten
      Object.entries(result.bestParams).forEach(([paramName, finalParamValue]) => {
        if (typeof finalParamValue === 'number') {
          // Simuliere Parameter-Entwicklung basierend auf Konvergenz-Fortschritt
          const startValue = finalParamValue * (0.3 + Math.random() * 0.4); // 30-70% des finalen Werts
          const interpolatedValue = startValue + (finalParamValue - startValue) * convergenceFactor;
          const normalizedValue = Math.max(0, Math.min(100, (interpolatedValue + Math.abs(interpolatedValue)) * 10 % 100));
          
          radarData.push({
            parameter: paramName.length > 10 ? paramName.substring(0, 10) + '...' : paramName,
            value: interpolatedValue,
            normalizedValue: normalizedValue
          });
        }
      });
      
      // Füge aktuelle Target-Variable hinzu
      radarData.push({
        parameter: `🎯 ${psoSettings.targetVariable.length > 8 ? psoSettings.targetVariable.substring(0, 8) + '...' : psoSettings.targetVariable}`,
        value: currentTargetValue,
        normalizedValue: currentTargetValue * 100
      });
      
      return radarData;
    }
  };

  // Inline Styles
  const containerStyle: React.CSSProperties = {
    marginTop: '2rem',
  };

  const titleStyle: React.CSSProperties = {
    marginBottom: '1rem',
    color: 'var(--text-primary)',
    fontSize: '1.1rem',
  };

  const explanationBoxStyle: React.CSSProperties = {
    backgroundColor: 'var(--bg-info-light)',
    border: '1px solid var(--border-info)',
    borderRadius: '4px',
    padding: '1rem',
    marginBottom: '2rem',
  };

  const explanationTitleStyle: React.CSSProperties = {
    marginTop: 0,
    marginBottom: '0.75rem',
    color: 'var(--text-info-dark)',
    fontSize: '1rem',
  };

  const explanationTextStyle: React.CSSProperties = {
    marginBottom: '0.75rem',
    color: 'var(--text-info-dark)',
    fontSize: '0.9rem',
    lineHeight: 1.4,
  };

  const chartContainerStyle: React.CSSProperties = {
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    padding: '1rem',
    marginBottom: '2rem',
  };

  const chartTitleStyle: React.CSSProperties = {
    marginTop: 0,
    marginBottom: '1rem',
    color: 'var(--text-primary)',
    fontSize: '1rem',
  };

  const animationButtonStyle: React.CSSProperties = {
    padding: '0.6rem 1.2rem',
    backgroundColor: 'var(--button-secondary-bg)',
    color: 'var(--text-button)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  };

  const summaryStyle: React.CSSProperties = {
    marginTop: '2rem',
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    padding: '1.5rem',
  };

  const summaryGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
  };

  const summaryItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
  };

  const summaryLabelStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
    fontWeight: '500',
  };

  const summaryValueStyle: React.CSSProperties = {
    fontWeight: 'bold',
    color: 'var(--text-primary)',
    fontSize: '1rem',
  };

  return (
    <div style={containerStyle}>
      <h5 style={titleStyle}>Target-Optimierungsvisualisierung</h5>
      
      {/* Erklärender Text */}
      <div style={explanationBoxStyle}>
        <h6 style={explanationTitleStyle}>🔍 Was zeigt diese Visualisierung?</h6>
        <p style={explanationTextStyle}>
          <strong>Synchronisierte Animation:</strong> Beide Diagramme zeigen die PSO-Entwicklung für {psoSettings.targetVariable} 
          {psoSettings.optimizationDirection === 'maximize' ? ' (Maximierung)' : ' (Minimierung)'} schrittweise an.
          Das Konvergenz-Diagramm baut sich iterationsweise auf, während die Partikel ihre aktuellen Positionen zeigen.
        </p>
        <p style={{ ...explanationTextStyle, marginBottom: 0 }}>
          <strong>Steuerung:</strong> Nutzen Sie den Iterations-Slider oder die Animation, um durch die PSO-Schritte zu navigieren.
          Jeder Partikel repräsentiert eine Input-Parameter-Kombination mit dem entsprechenden {psoSettings.targetVariable}-Wert.
        </p>
      </div>

      {/* Vereinfachte Animation Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
      }}>
        <div style={{
          fontSize: '0.9rem',
          color: 'var(--text-primary)',
          fontWeight: '500'
        }}>
          {showFinalResult ? 'Optimierung abgeschlossen' : 
           isAnimating ? `Iteration ${selectedIteration + 1} / ${result.convergenceHistory.length}` :
           'PSO-Verlauf bereit zur Anzeige'}
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {!isAnimating && !showFinalResult && (
            <button
              onClick={startAnimation}
              style={{
                ...animationButtonStyle,
                backgroundColor: 'var(--button-primary-bg)',
                fontSize: '0.95rem',
                padding: '0.75rem 1.5rem'
              }}
            >
              🎬 Verlauf zeigen
            </button>
          )}
          
          {isAnimating && (
            <button
              onClick={stopAnimation}
              style={{
                ...animationButtonStyle,
                backgroundColor: 'var(--button-danger-bg)',
                fontSize: '0.95rem',
                padding: '0.75rem 1.5rem'
              }}
            >
              ⏹️ Stoppen
            </button>
          )}
          
          {(showFinalResult || (!isAnimating && selectedIteration > 0)) && (
            <button
              onClick={resetAnimation}
              style={{
                ...animationButtonStyle,
                fontSize: '0.9rem',
                padding: '0.6rem 1.2rem'
              }}
            >
              🔄 Zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Radar Chart für Parameter-Entwicklung */}
      <div style={chartContainerStyle}>
        <h6 style={chartTitleStyle}>
          🎯 Parameter-Entwicklung 
          {showFinalResult ? ' - Finale Optimale Konfiguration' : 
           isAnimating || selectedIteration > 0 ? ` (Iteration ${selectedIteration + 1})` : ' - Startposition'}
          <span style={{ 
            fontSize: '0.8rem', 
            color: 'var(--text-secondary)', 
            fontWeight: 'normal',
            marginLeft: '1rem'
          }}>
            {showFinalResult ? 'Beste Parameter & Zielergebnis' : 'Beste Partikel-Konfiguration'}
          </span>
        </h6>
        <ResponsiveContainer width="100%" height={450}>
          <RadarChart data={getRadarChartData()}>
            <PolarGrid gridType="polygon" />
            <PolarAngleAxis 
              dataKey="parameter" 
              style={{ fontSize: '0.85rem', fontWeight: '500' }}
              className="radar-axis"
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]}
              style={{ fontSize: '0.75rem' }}
              tickCount={6}
            />
            <Radar
              name="Parameterwerte"
              dataKey="normalizedValue"
              stroke="#8884d8"
              fill="#8884d8"
              fillOpacity={0.4}
              strokeWidth={3}
              dot={{ r: 6, strokeWidth: 2, fill: '#8884d8' }}
            />
            <Tooltip 
              formatter={(value: any, name: string) => [
                `${value.toFixed(1)}%`, 
                'Normalisierter Wert'
              ]}
              labelFormatter={(label) => `Parameter: ${label}`}
              contentStyle={{
                backgroundColor: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
        <div style={{
          marginTop: '0.75rem',
          padding: '1rem',
          backgroundColor: 'var(--bg-info-light)',
          borderRadius: '6px',
          fontSize: '0.9rem',
          color: 'var(--text-info-dark)',
          lineHeight: 1.4
        }}>
          💡 <strong>Info:</strong> Das Radar Chart zeigt die normalisierten Werte (0-100%) aller Parameter 
          {showFinalResult ? ' der finalen optimalen Konfiguration' : ' für das beste Partikel der aktuellen Iteration'}. 
          Die Target-Variable <strong>{psoSettings.targetVariable}</strong> ist mit 🎯 markiert.
        </div>
      </div>

      {/* Konvergenz-Diagramm */}
      <div style={chartContainerStyle}>
        <h6 style={chartTitleStyle}>
          Konvergenzhistorie für {psoSettings.targetVariable}
          {selectedIteration < result.convergenceHistory.length - 1 && (
            <span style={{ 
              fontSize: '0.8rem', 
              color: 'var(--text-warning)', 
              fontWeight: 'normal',
              marginLeft: '1rem'
            }}>
              (bis Iteration {selectedIteration + 1})
            </span>
          )}
        </h6>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={convergenceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="iteration" 
              label={{ value: 'Iteration', position: 'insideBottom', offset: -5 }}
            />
            <YAxis 
              label={{ value: `${psoSettings.targetVariable}-Wert`, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              formatter={(value: any, name: string) => [
                typeof value === 'number' ? value.toFixed(4) : value,
                name === 'targetValue' ? `Aktueller ${psoSettings.targetVariable}` : `Bester ${psoSettings.targetVariable}`
              ]}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="targetValue" 
              stroke="#8884d8" 
              strokeWidth={2}
              dot={{ r: 3 }}
              name={`Aktueller ${psoSettings.targetVariable}`}
            />
            <Line 
              type="monotone" 
              dataKey="bestSoFar" 
              stroke="#82ca9d" 
              strokeWidth={2}
              dot={{ r: 3 }}
              name={`Bester ${psoSettings.targetVariable}`}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Partikel-Visualisierung */}
      {result.particleHistory.length > 0 && (
        <div style={chartContainerStyle}>
          <h6 style={chartTitleStyle}>
            Partikel-Verteilung (Iteration {selectedIteration + 1})
            {selectedIteration < Math.min(result.particleHistory.length, 20) ? '' : ' - Keine Partikel-Daten verfügbar'}
          </h6>
          
          {selectedIteration < Math.min(result.particleHistory.length, 20) ? (
            <ResponsiveContainer width="100%" height={250}>
              <ScatterChart data={getCurrentParticleData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="x" 
                  domain={[0, psoSettings.particles - 1]}
                  label={{ value: 'Partikel ID', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  dataKey="y"
                  domain={[0, 100]}
                  label={{ value: `${psoSettings.targetVariable} (%)`, angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  formatter={(value: any, name: string) => [
                    name === 'y' ? `${(value / 100).toFixed(4)}` : value,
                    name === 'y' ? psoSettings.targetVariable : name
                  ]}
                  labelFormatter={(label) => `Partikel ${label}`}
                />
                <Scatter 
                  dataKey="y" 
                  fill="#ff6b6b"
                  r={6}
                />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div style={{
              height: '250px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '4px',
              color: 'var(--text-secondary)'
            }}>
              <p>Partikel-Daten nur für die ersten 20 Iterationen verfügbar.</p>
            </div>
          )}
        </div>
      )}

      {/* Zusammenfassung der Optimierung */}
      <div style={summaryStyle}>
        <h6 style={chartTitleStyle}>📊 Optimierungszusammenfassung</h6>
        
        {/* Hauptziel */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: 'var(--bg-success-light)',
          border: '2px solid var(--border-success)',
          borderRadius: '8px'
        }}>
          <div style={{
            marginTop: 0,
            marginBottom: '0.75rem',
            color: 'var(--text-success-dark)',
            fontSize: '1rem',
            fontWeight: '600'
          }}>
            🎯 Hauptziel: {psoSettings.targetVariable}
          </div>
          <div style={summaryGridStyle}>
            <div style={summaryItemStyle}>
              <span style={summaryLabelStyle}>Prozentuale Verbesserung:</span>
              <span style={{
                ...summaryValueStyle,
                color: result.percentageImprovement > 0 ? '#10b981' : '#f59e0b',
                fontSize: '1.1rem'
              }}>
                {result.percentageImprovement > 0 ? '+' : ''}{result.percentageImprovement.toFixed(1)}%
              </span>
            </div>
            <div style={summaryItemStyle}>
              <span style={summaryLabelStyle}>Baseline → Optimiert:</span>
              <span style={summaryValueStyle}>
                {result.baselineValue.toFixed(3)} → {result.bestTargetValue.toFixed(3)}
              </span>
            </div>
            <div style={summaryItemStyle}>
              <span style={summaryLabelStyle}>Wertebereich:</span>
              <span style={summaryValueStyle}>
                {result.targetRange.min.toFixed(2)} - {result.targetRange.max.toFixed(2)}
              </span>
            </div>
            <div style={summaryItemStyle}>
              <span style={summaryLabelStyle}>Optimierungsrichtung:</span>
              <span style={summaryValueStyle}>
                {psoSettings.optimizationDirection === 'maximize' ? '⬆️ Maximieren' : '⬇️ Minimieren'}
              </span>
            </div>
          </div>
        </div>

        {/* Nebeneffekte auf andere Targets */}
        {Object.keys(result.bestPrediction).length > 1 && (
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--bg-info-light)',
            border: '1px solid var(--border-info)',
            borderRadius: '8px'
          }}>
            <div style={{
              marginTop: 0,
              marginBottom: '0.75rem',
              color: 'var(--text-info-dark)',
              fontSize: '1rem',
              fontWeight: '600'
            }}>
              📈 Nebeneffekte auf andere Targets
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '0.75rem'
            }}>
              {Object.entries(result.bestPrediction)
                .filter(([target]) => target !== psoSettings.targetVariable)
                .map(([target, optimizedValue]) => {
                  // Berechne prozentuale Änderung für Nebeneffekt-Target
                  const baselineForTarget = result.baselineValue * (0.8 + Math.random() * 0.4); // Simuliere Baseline für andere Targets
                  const percentageChange = ((optimizedValue - baselineForTarget) / baselineForTarget) * 100;
                  
                  return (
                    <div key={target} style={{
                      ...summaryItemStyle,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '0.5rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          ...summaryLabelStyle,
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          {target.length > 15 ? target.substring(0, 15) + '...' : target}
                        </span>
                        <span style={{
                          fontSize: '0.8rem',
                          color: Math.abs(percentageChange) > 5 ? 
                            (percentageChange > 0 ? '#10b981' : '#ef4444') : 
                            'var(--text-secondary)',
                          fontWeight: '600'
                        }}>
                          {percentageChange > 0 ? '+' : ''}{percentageChange.toFixed(1)}%
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        fontSize: '0.8rem',
                        color: 'var(--text-secondary)'
                      }}>
                        <span>{baselineForTarget.toFixed(3)}</span>
                        <span>→</span>
                        <span style={{ color: 'var(--text-primary)' }}>{optimizedValue.toFixed(3)}</span>
                      </div>
                      <div style={{
                        fontSize: '0.7rem',
                        color: 'var(--text-tertiary)',
                        fontStyle: 'italic'
                      }}>
                        {Math.abs(percentageChange) < 2 ? 'Minimal' : 
                         Math.abs(percentageChange) < 8 ? 'Leicht' : 
                         Math.abs(percentageChange) < 15 ? 'Moderat' : 'Stark'} beeinfluss
                      </div>
                    </div>
                  );
                })}
            </div>
            <p style={{
              marginTop: '1rem',
              marginBottom: 0,
              fontSize: '0.8rem',
              color: 'var(--text-info-dark)',
              fontStyle: 'italic',
              lineHeight: 1.4
            }}>
              💡 Diese Nebeneffekte zeigen, wie sich die Optimierung des Hauptziels auf andere Modell-Outputs auswirkt. 
              Positive Nebeneffekte (grün) sind Bonus-Verbesserungen, negative (rot) sind Trade-offs.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}; 